import React, { useEffect, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { ArrowLeft } from 'lucide-react';
import { Reveal, FadeIn } from '@/components/motion';
import { OrnamentWave } from '@/components/Ornaments';
import { SITE } from '@/components/Seo';
import { ShareBar } from '@/components/ShareBar';
import { useSwitch } from '@/context/SwitchContext';
import { api } from '@/lib/api';

function renderContent(content) {
  const blocks = content.split('\n\n');
  return blocks.map((block, i) => {
    if (block.startsWith('## ')) return <h2 key={i}>{block.replace('## ', '')}</h2>;
    return <p key={i}>{block}</p>;
  });
}

export default function BlogPost() {
  const { slug } = useParams();
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const navigate = useNavigate();
  const { setOverride } = useSwitch();
  const [post, setPost] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    let active = true;
    api.get(`/blog/${slug}?lang=${lang}`)
      .then((r) => { if (active) setPost(r.data); })
      .catch(() => { if (active) setNotFound(true); });
    return () => { active = false; };
  }, [slug, lang]);

  useEffect(() => {
    if (post) {
      const other = lang === 'it' ? 'en' : 'it';
      setOverride(`/${other}/blog/${post.slug_other}`);
    }
    return () => setOverride(null);
  }, [post, lang, setOverride]);

  if (notFound) {
    return (
      <div className="px-5 md:px-10 py-32 text-center" data-testid="blogpost-notfound">
        <p className="font-serif text-3xl text-textDark mb-6">404</p>
        <Link to={`/${lang}/blog`} className="text-terracotta font-semibold">{t('common.backToBlog')}</Link>
      </div>
    );
  }
  if (!post) return <div className="px-5 md:px-10 py-32 text-center text-textDark/60 text-xl">{t('blog.loading')}</div>;

  const itSlug = lang === 'it' ? post.slug : post.slug_other;
  const enSlug = lang === 'en' ? post.slug : post.slug_other;

  return (
    <article data-testid="blogpost-page">
      <Helmet>
        <html lang={lang} />
        <title>{post.title}</title>
        <meta name="description" content={post.excerpt} />
        <link rel="canonical" href={`${SITE}/${lang}/blog/${post.slug}`} />
        <link rel="alternate" hrefLang="it" href={`${SITE}/it/blog/${itSlug}`} />
        <link rel="alternate" hrefLang="en" href={`${SITE}/en/blog/${enSlug}`} />
        <link rel="alternate" hrefLang="x-default" href={`${SITE}/it/blog/${itSlug}`} />
        <meta property="og:title" content={post.title} />
        <meta property="og:description" content={post.excerpt} />
        <meta property="og:type" content="article" />
        <meta property="og:image" content={post.cover_image} />
        <meta property="og:url" content={`${SITE}/${lang}/blog/${post.slug}`} />
      </Helmet>
      <div className="px-5 md:px-10 pt-12 pb-6">
        <div className="max-w-[760px] mx-auto">
          <button onClick={() => navigate(`/${lang}/blog`)} data-testid="blogpost-back" className="inline-flex items-center gap-2 text-terracotta font-semibold mb-8 hover:gap-3 transition-[gap]">
            <ArrowLeft size={18} /> {t('common.backToBlog')}
          </button>
          <FadeIn>
            <h1 className="font-serif text-4xl md:text-5xl leading-[1.08] text-textDark">{post.title}</h1>
          </FadeIn>
        </div>
      </div>

      <div className="px-5 md:px-10">
        <Reveal className="max-w-[1000px] mx-auto">
          <div className="rounded-[2rem] overflow-hidden border-[10px] border-rosa">
            <img src={post.cover_image} alt="" data-testid="blogpost-cover" className="w-full h-[280px] md:h-[440px] object-cover" />
          </div>
        </Reveal>
      </div>

      <div className="px-5 md:px-10 py-14">
        <div className="max-w-[720px] mx-auto editorial-content text-textDark">
          {renderContent(post.content)}
          <div className="mt-12 pt-8 border-t border-terracotta/15">
            <ShareBar url={`${SITE}/${lang}/blog/${post.slug}`} title={post.title} />
          </div>
          <div className="mt-10 text-terracotta/50"><OrnamentWave className="w-48 h-auto" /></div>
        </div>
      </div>
    </article>
  );
}
